import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading3, Undo2, Redo2, RemoveFormatting } from "lucide-react";

const btn = (active) =>
  `grid place-items-center h-8 w-8 rounded-lg transition-colors ${active ? "bg-brand text-brand-cream" : "text-brand-ink/60 hover:bg-brand-soft/70"}`;

// Rich text editor for product descriptions — supports paste of formatted content; keeps the site font.
export const RichTextEditor = ({ value, onChange, placeholder, testid }) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "rich-desc min-h-[180px] px-4 py-3 focus:outline-none",
        "data-testid": testid,
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-brand/20 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-brand/40" data-testid={`${testid}-wrapper`}>
      <div className="flex items-center gap-0.5 border-b border-brand/10 bg-stone-alt/40 px-2 py-1.5 flex-wrap">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} data-testid={`${testid}-bold`} aria-label="Bold">
          <Bold className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} data-testid={`${testid}-italic`} aria-label="Italic">
          <Italic className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive("underline"))} data-testid={`${testid}-underline`} aria-label="Underline">
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <span className="w-px h-5 bg-brand/15 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive("heading", { level: 3 }))} data-testid={`${testid}-heading`} aria-label="Heading">
          <Heading3 className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} data-testid={`${testid}-bullets`} aria-label="Bullet list">
          <List className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} data-testid={`${testid}-numbers`} aria-label="Numbered list">
          <ListOrdered className="h-4 w-4" />
        </button>
        <span className="w-px h-5 bg-brand/15 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} className={btn(false)} data-testid={`${testid}-clear`} aria-label="Clear formatting">
          <RemoveFormatting className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn(false)} aria-label="Undo">
          <Undo2 className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn(false)} aria-label="Redo">
          <Redo2 className="h-4 w-4" />
        </button>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export default RichTextEditor;
